import React, { useState, useEffect, useCallback, useRef, useContext } from "react";
import axios from "axios";
import { API, AuthContext } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Package, Plus, Search, Edit, Trash2, AlertTriangle, ArrowRightLeft,
  Warehouse, Wrench, Loader2, Box, Tag, X, Lock, Sparkles,
  Download, Upload, TrendingDown, DollarSign, BarChart3, User, MapPin,
  ImageIcon, Copy, Eraser,
} from "lucide-react";
import { toast } from "sonner";
import { usePlanLimits } from "../../contexts/PlanLimitContext";
import { useNavigate } from "react-router-dom";
import ImportInventoryModal from "../../components/inventory/ImportInventoryModal";
import ProductPhotoUpload from "../../components/inventory/ProductPhotoUpload";
import CloneLocationModal from "../../components/inventory/CloneLocationModal";
import TransferStockModal from "../../components/inventory/TransferStockModal";
import CleanupTab from "../../components/inventory/CleanupTab";

const getAuthConfig = () => {
  const token = localStorage.getItem("token");
  return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

/* ══════════════ LOW STOCK DASHBOARD ══════════════ */
const LowStockDashboard = ({ items }) => {
  const lowItems = items.filter(i => i.stock_quantity <= (i.min_stock_threshold || 0));
  if (lowItems.length === 0) return null;

  return (
    <div className="rounded-xl border border-orange-500/30 bg-orange-500/5 backdrop-blur-sm p-4 mb-4" data-testid="low-stock-dashboard">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-lg bg-orange-500/15 flex items-center justify-center">
          <AlertTriangle className="w-4 h-4 text-orange-400" />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-foreground font-['Manrope']">Low Stock Alert</h3>
          <p className="text-xs text-muted-foreground">{lowItems.length} item{lowItems.length > 1 ? "s" : ""} need restocking</p>
        </div>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
        {lowItems.slice(0, 8).map(item => (
          <div key={item.item_id} className="flex-shrink-0 rounded-lg border border-orange-500/20 bg-background/60 px-3 py-2 min-w-[140px]" data-testid={`low-stock-${item.item_id}`}>
            <p className="text-xs font-medium text-foreground truncate">{item.name}</p>
            <div className="flex items-center justify-between mt-1">
              <span className="text-lg font-bold text-orange-400 font-['Manrope']">{item.stock_quantity}</span>
              <span className="text-[10px] text-muted-foreground">min {item.min_stock_threshold}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ══════════════ ITEMS TAB ══════════════ */
const ItemsTab = ({ isStoreAccount, storeLocations }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [locationFilter, setLocationFilter] = useState("all");
  const [categories, setCategories] = useState([]);
  const [showLowStock, setShowLowStock] = useState(() => new URLSearchParams(window.location.search).get("low_stock") === "1");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ name: "", sku: "", description: "", category: "", unit_price: 0, cost_price: 0, unit: "each", stock_quantity: 0, min_stock_threshold: 5, low_stock_alert_email: false, low_stock_alert_sms: false, location_id: "" });
  const [catDialogOpen, setCatDialogOpen] = useState(false);
  const [newCatName, setNewCatName] = useState("");
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [cloneModalOpen, setCloneModalOpen] = useState(false);
  const [transferItem, setTransferItem] = useState(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (categoryFilter !== "all") params.append("category", categoryFilter);
      if (showLowStock) params.append("low_stock", "true");
      if (locationFilter !== "all") params.append("location_id", locationFilter);
      const res = await axios.get(`${API}/inventory/items?${params}`, getAuthConfig());
      setItems(res.data);
    } catch { toast.error("Failed to load inventory"); }
    finally { setLoading(false); }
  }, [search, categoryFilter, showLowStock, locationFilter]);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/inventory/categories`, getAuthConfig());
      setCategories(res.data);
    } catch {}
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);
  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const openCreate = () => {
    setEditItem(null);
    setForm({ name: "", sku: "", description: "", category: "", unit_price: 0, cost_price: 0, unit: "each", stock_quantity: 0, min_stock_threshold: 5, low_stock_alert_email: false, low_stock_alert_sms: false, location_id: locationFilter !== "all" ? locationFilter : "" });
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditItem(item);
    setForm({ name: item.name, sku: item.sku || "", description: item.description || "", category: item.category || "", unit_price: item.unit_price || 0, cost_price: item.cost_price || 0, unit: item.unit || "each", stock_quantity: item.stock_quantity || 0, min_stock_threshold: item.min_stock_threshold || 5, low_stock_alert_email: item.low_stock_alert_email || false, low_stock_alert_sms: item.low_stock_alert_sms || false, location_id: item.location_id || "", photo_url: item.photo_url || null, photo_thumbnail_url: item.photo_thumbnail_url || null });
    setDialogOpen(true);
  };

  const saveItem = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    try {
      if (editItem) {
        await axios.put(`${API}/inventory/items/${editItem.item_id}`, form, getAuthConfig());
        toast.success("Item updated");
      } else {
        await axios.post(`${API}/inventory/items`, form, getAuthConfig());
        toast.success("Item created");
      }
      setDialogOpen(false);
      fetchItems();
      fetchCategories();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save item"); }
  };

  const deleteItem = async (itemId) => {
    if (!window.confirm("Delete this item? This will also remove it from all technician inventories.")) return;
    try {
      await axios.delete(`${API}/inventory/items/${itemId}`, getAuthConfig());
      toast.success("Item deleted");
      fetchItems();
    } catch { toast.error("Failed to delete"); }
  };

  const addCategory = async () => {
    const name = newCatName.trim();
    if (!name) return;
    try {
      await axios.post(`${API}/inventory/categories`, { name }, getAuthConfig());
      toast.success(`Category "${name}" added`);
      setNewCatName("");
      fetchCategories();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to add category"); }
  };

  const deleteCategory = async (name) => {
    try {
      await axios.delete(`${API}/inventory/categories/${encodeURIComponent(name)}`, getAuthConfig());
      toast.success(`Category "${name}" removed`);
      fetchCategories();
    } catch { toast.error("Failed to delete category"); }
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (locationFilter && locationFilter !== "all") {
        params.append("location_id", locationFilter);
      }
      const qs = params.toString() ? `?${params.toString()}` : "";
      const res = await axios.get(`${API}/inventory/export${qs}`, { ...getAuthConfig(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      const today = new Date().toISOString().split("T")[0];
      // Add location suffix so users can tell exports apart
      let locSuffix = "";
      if (locationFilter && locationFilter !== "all") {
        const loc = storeLocations.find((l) => l.location_id === locationFilter);
        if (loc?.code) locSuffix = `-${loc.code}`;
        else if (loc?.name) locSuffix = `-${loc.name.replace(/[^a-z0-9]+/gi, "_")}`;
      }
      a.download = `fieldz-inventory${locSuffix}-${today}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Inventory exported");
    } catch { toast.error("Export failed"); }
  };

  const handleImportComplete = (result) => {
    if (result) {
      const total = (result.created || 0) + (result.updated || 0);
      if (total > 0) {
        fetchItems();
        fetchCategories();
      }
    }
  };

  // Stats
  const totalValue = items.reduce((s, i) => s + (i.unit_price || 0) * (i.stock_quantity || 0), 0);
  const lowCount = items.filter(i => i.stock_quantity <= (i.min_stock_threshold || 0)).length;

  return (
    <div className="space-y-4" data-testid="items-tab">
      {/* Low Stock Dashboard */}
      <LowStockDashboard items={items} />

      {/* Filters + Actions */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Search items..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10 bg-card/60 backdrop-blur-sm border-border/40 h-9" data-testid="inventory-search" />
        </div>
        <div className="flex flex-wrap gap-2">
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[140px] h-9 bg-card/60 backdrop-blur-sm border-border/40" data-testid="category-filter"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent className="bg-popover border-theme">
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          {isStoreAccount && storeLocations.length > 0 && (
            <Select value={locationFilter} onValueChange={setLocationFilter}>
              <SelectTrigger className="w-[160px] h-9 bg-card/60 backdrop-blur-sm border-border/40" data-testid="location-filter">
                <SelectValue placeholder="Location" />
              </SelectTrigger>
              <SelectContent className="bg-popover border-theme">
                <SelectItem value="all">All Locations</SelectItem>
                {storeLocations.map(loc => (
                  <SelectItem key={loc.location_id} value={loc.location_id}>
                    <span className="flex items-center gap-1.5">
                      <MapPin className="w-3 h-3 text-blue-400" /> {loc.name}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button variant={showLowStock ? "default" : "outline"} size="sm" onClick={() => setShowLowStock(!showLowStock)} className={`h-9 ${showLowStock ? "bg-orange-500 text-white hover:bg-orange-600" : "border-border/40 bg-card/60 backdrop-blur-sm"}`} data-testid="low-stock-filter">
            <AlertTriangle className="w-3.5 h-3.5 mr-1.5" />{lowCount > 0 && <span className="mr-1">{lowCount}</span>}Low
          </Button>
          <Button variant="outline" size="sm" onClick={() => setCatDialogOpen(true)} className="h-9 border-border/40 bg-card/60 backdrop-blur-sm" data-testid="manage-categories-btn">
            <Tag className="w-3.5 h-3.5 mr-1.5" />Tags
          </Button>
          <Button variant="outline" size="sm" onClick={handleExport} disabled={!items.length} className="h-9 border-border/40 bg-card/60 backdrop-blur-sm" data-testid="export-btn">
            <Download className="w-3.5 h-3.5 mr-1.5" />Export
          </Button>
          <Button variant="outline" size="sm" onClick={() => setImportModalOpen(true)} className="h-9 border-border/40 bg-card/60 backdrop-blur-sm" data-testid="import-btn">
            <Upload className="w-3.5 h-3.5 mr-1.5" />Import
          </Button>
          {isStoreAccount && storeLocations.length >= 2 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCloneModalOpen(true)}
              className="h-9 border-border/40 bg-card/60 backdrop-blur-sm"
              data-testid="clone-btn"
              title="Copy inventory from one location to another"
            >
              <Copy className="w-3.5 h-3.5 mr-1.5" />Clone
            </Button>
          )}
          <Button onClick={openCreate} className="h-9 bg-[#0046BE] text-white hover:bg-[#0046BE]/80" data-testid="add-item-btn">
            <Plus className="w-4 h-4 mr-1.5" />Add Item
          </Button>
        </div>
      </div>

      <ImportInventoryModal
        open={importModalOpen}
        onOpenChange={setImportModalOpen}
        onImported={handleImportComplete}
        isStoreAccount={isStoreAccount}
        storeLocations={storeLocations}
        currentLocationId={locationFilter !== "all" ? locationFilter : ""}
      />

      <CloneLocationModal
        open={cloneModalOpen}
        onOpenChange={setCloneModalOpen}
        storeLocations={storeLocations}
        currentLocationId={locationFilter !== "all" ? locationFilter : ""}
        onCloned={() => { fetchItems(); fetchCategories(); }}
      />

      <TransferStockModal
        open={!!transferItem}
        onOpenChange={(o) => { if (!o) setTransferItem(null); }}
        item={transferItem}
        storeLocations={storeLocations}
        onTransferred={() => { fetchItems(); setTransferItem(null); }}
      />

      {/* Items Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mr-2" />Loading inventory...</div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
          <div className="w-16 h-16 rounded-2xl bg-muted/30 flex items-center justify-center mb-4"><Package className="w-8 h-8 opacity-30" /></div>
          <p className="text-sm font-medium">{search || categoryFilter !== "all" || showLowStock ? "No items match your filters" : "No inventory items yet"}</p>
          <p className="text-xs mt-1 opacity-60">Add items manually or import from CSV</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="items-grid">
          {items.map((item, idx) => {
            const isLow = item.stock_quantity <= (item.min_stock_threshold || 0);
            return (
              <div
                key={item.item_id}
                className={`group relative overflow-hidden rounded-xl bg-card/60 backdrop-blur-md border transition-all duration-300 ease-out hover:translate-y-[-2px] hover:shadow-lg ${isLow ? "border-orange-500/40 hover:border-orange-500/60" : "border-border/60 hover:border-border"}`}
                style={{ animationDelay: `${idx * 30}ms` }}
                data-testid={`item-card-${item.item_id}`}
              >
                {/* Top accent */}
                {isLow && <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-orange-500 to-amber-500" />}

                <div className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      {item.photo_thumbnail_url || item.photo_url ? (
                        <img
                          src={(item.photo_thumbnail_url || item.photo_url).startsWith("http")
                            ? (item.photo_thumbnail_url || item.photo_url)
                            : `${API.replace(/\/api$/, "")}${item.photo_thumbnail_url || item.photo_url}`}
                          alt={item.name}
                          className="w-10 h-10 rounded-xl object-cover flex-shrink-0 border border-border/40"
                          data-testid={`item-photo-${item.item_id}`}
                          onError={(e) => { e.currentTarget.style.display = "none"; }}
                        />
                      ) : (
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isLow ? "bg-orange-500/10" : "bg-[#0046BE]/10"}`}>
                          <Package className={`w-5 h-5 ${isLow ? "text-orange-400" : "text-[#0046BE]"}`} />
                        </div>
                      )}
                      <div className="min-w-0">
                        <h3 className="font-semibold text-sm text-foreground truncate font-['Manrope']">{item.name}</h3>
                        <p className="text-xs text-muted-foreground">{item.sku}</p>
                      </div>
                    </div>
                    <div className="flex gap-0.5 ml-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => openEdit(item)} className="p-1.5 rounded-lg hover:bg-muted/60 text-blue-400" data-testid={`edit-item-${item.item_id}`}><Edit className="w-3.5 h-3.5" /></button>
                      {isStoreAccount && storeLocations.length >= 2 && (Number(item.stock_quantity) || 0) > 0 && (
                        <button
                          onClick={() => setTransferItem(item)}
                          className="p-1.5 rounded-lg hover:bg-muted/60 text-purple-400"
                          title="Transfer stock"
                          data-testid={`transfer-${item.item_id}`}
                        >
                          <ArrowRightLeft className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <button onClick={() => deleteItem(item.item_id)} className="p-1.5 rounded-lg hover:bg-muted/60 text-red-400" data-testid={`delete-${item.item_id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>

                  {item.category && <Badge className="bg-muted/60 text-muted-foreground text-[10px] mb-3 border-0">{item.category}</Badge>}
                  {item.location_id && isStoreAccount && (() => {
                    const loc = storeLocations.find(l => l.location_id === item.location_id);
                    return loc ? <Badge className="bg-blue-500/10 text-blue-500 text-[10px] mb-3 border-0 ml-1"><MapPin className="w-2.5 h-2.5 mr-0.5" />{loc.name}</Badge> : null;
                  })()}

                  <div className="grid grid-cols-3 gap-2">
                    <div className={`rounded-lg p-2.5 text-center ${isLow ? "bg-orange-500/8" : "bg-muted/40"}`}>
                      <p className={`text-lg font-bold font-['Manrope'] ${isLow ? "text-orange-400" : "text-foreground"}`}>{item.stock_quantity}</p>
                      <p className="text-[10px] text-muted-foreground">Stock</p>
                    </div>
                    <div className="bg-muted/40 rounded-lg p-2.5 text-center">
                      <p className="text-lg font-bold text-foreground font-['Manrope']">${item.unit_price}</p>
                      <p className="text-[10px] text-muted-foreground">Price</p>
                    </div>
                    <div className="bg-muted/40 rounded-lg p-2.5 text-center">
                      <p className="text-lg font-bold text-foreground font-['Manrope']">${(item.unit_price * item.stock_quantity).toFixed(0)}</p>
                      <p className="text-[10px] text-muted-foreground">Value</p>
                    </div>
                  </div>

                  {isLow && (
                    <div className="flex items-center gap-1.5 mt-3 text-orange-400 text-xs font-medium">
                      <TrendingDown className="w-3.5 h-3.5" />Below threshold ({item.min_stock_threshold})
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-background border-theme text-foreground max-w-lg" data-testid="item-dialog">
          <DialogHeader><DialogTitle className="text-foreground font-['Manrope']">{editItem ? "Edit Item" : "Add Item"}</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground font-medium">Name *</label>
                <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="bg-muted/50 border-border/40 h-9 mt-1" data-testid="item-name" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground font-medium">SKU</label>
                <Input value={form.sku} onChange={e => setForm({...form, sku: e.target.value})} placeholder="Auto-generated" className="bg-muted/50 border-border/40 h-9 mt-1" data-testid="item-sku" />
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground font-medium">Description</label>
              <Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="bg-muted/50 border-border/40 h-9 mt-1" />
            </div>
            {isStoreAccount && storeLocations.length > 0 && (
              <div>
                <label className="text-xs text-muted-foreground font-medium flex items-center gap-1"><MapPin className="w-3 h-3 text-blue-400" /> Store Location</label>
                <Select value={form.location_id || "_none_"} onValueChange={v => setForm({...form, location_id: v === "_none_" ? "" : v})}>
                  <SelectTrigger className="bg-muted/50 border-border/40 h-9 mt-1" data-testid="item-location"><SelectValue placeholder="Select location" /></SelectTrigger>
                  <SelectContent className="bg-popover border-theme">
                    <SelectItem value="_none_">No Location (Warehouse)</SelectItem>
                    {storeLocations.map(loc => (
                      <SelectItem key={loc.location_id} value={loc.location_id}>
                        <span className="flex items-center gap-1.5"><MapPin className="w-3 h-3 text-blue-400" />{loc.name}{loc.code ? ` (${loc.code})` : ""}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground font-medium">Category</label>
                <Select value={form.category || "_none_"} onValueChange={v => setForm({...form, category: v === "_none_" ? "" : v})}>
                  <SelectTrigger className="bg-muted/50 border-border/40 h-9 mt-1" data-testid="item-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent className="bg-popover border-theme">
                    <SelectItem value="_none_">No category</SelectItem>
                    {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground font-medium">Unit</label>
                <Input value={form.unit} onChange={e => setForm({...form, unit: e.target.value})} placeholder="each, box, pack" className="bg-muted/50 border-border/40 h-9 mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground font-medium">Sale Price ($)</label>
                <Input type="number" step="0.01" value={form.unit_price} onChange={e => setForm({...form, unit_price: parseFloat(e.target.value) || 0})} className="bg-muted/50 border-border/40 h-9 mt-1" data-testid="item-price" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground font-medium">Cost Price ($)</label>
                <Input type="number" step="0.01" value={form.cost_price} onChange={e => setForm({...form, cost_price: parseFloat(e.target.value) || 0})} className="bg-muted/50 border-border/40 h-9 mt-1" data-testid="item-cost" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground font-medium">Stock Quantity</label>
                <Input type="number" value={form.stock_quantity} onChange={e => setForm({...form, stock_quantity: parseInt(e.target.value) || 0})} className="bg-muted/50 border-border/40 h-9 mt-1" data-testid="item-stock" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground font-medium">Low Stock Threshold</label>
                <Input type="number" value={form.min_stock_threshold} onChange={e => setForm({...form, min_stock_threshold: parseInt(e.target.value) || 0})} className="bg-muted/50 border-border/40 h-9 mt-1" data-testid="item-threshold" />
              </div>
            </div>
            {editItem && (
              <div className="pt-2 border-t border-border/40">
                <ProductPhotoUpload
                  itemId={editItem.item_id}
                  photoUrl={form.photo_url}
                  onChange={(patch) => {
                    setForm((f) => ({ ...f, ...patch }));
                    setItems((curr) => curr.map((i) =>
                      i.item_id === editItem.item_id ? { ...i, ...patch } : i
                    ));
                  }}
                />
              </div>
            )}
            {!editItem && (
              <p className="text-[11px] text-muted-foreground border-t border-border/40 pt-2">
                Save the item first to add a photo.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-theme h-9">Cancel</Button>
            <Button onClick={saveItem} className="bg-[#0046BE] text-white hover:bg-[#0046BE]/80 h-9" data-testid="save-item-btn">{editItem ? "Update" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Categories Dialog */}
      <Dialog open={catDialogOpen} onOpenChange={setCatDialogOpen}>
        <DialogContent className="bg-background border-theme text-foreground max-w-sm" data-testid="categories-dialog">
          <DialogHeader><DialogTitle className="text-foreground flex items-center gap-2 font-['Manrope']"><Tag className="w-5 h-5" />Manage Categories</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              <Input value={newCatName} onChange={e => setNewCatName(e.target.value)} onKeyDown={e => { if (e.key === "Enter") addCategory(); }} placeholder="New category name..." className="bg-muted/50 border-border/40 h-9 flex-1" data-testid="new-category-input" />
              <Button onClick={addCategory} size="sm" className="h-9 bg-[#0046BE] text-white hover:bg-[#0046BE]/80" data-testid="add-category-btn"><Plus className="w-4 h-4" /></Button>
            </div>
            <div className="max-h-[40vh] overflow-y-auto space-y-1">
              {categories.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No categories yet</p>
              ) : categories.map(cat => (
                <div key={cat} className="flex items-center justify-between bg-muted/30 rounded-md px-3 py-2 group" data-testid={`category-${cat}`}>
                  <span className="text-sm text-foreground">{cat}</span>
                  <button onClick={() => deleteCategory(cat)} className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-red-400" data-testid={`delete-category-${cat}`}><X className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};


/* ══════════════ TECHNICIAN STOCK TAB ══════════════ */
const TechStockTab = () => {
  const [technicians, setTechnicians] = useState([]);
  const [selectedTech, setSelectedTech] = useState(null);
  const [techStock, setTechStock] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignForm, setAssignForm] = useState({ item_id: "", quantity: 1 });
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferForm, setTransferForm] = useState({ item_id: "", quantity: 1, from_type: "warehouse", from_id: "", to_type: "technician", to_id: "", notes: "" });

  useEffect(() => {
    const fetchTechs = async () => {
      try {
        const res = await axios.get(`${API}/technicians`, getAuthConfig());
        setTechnicians(res.data);
      } catch {}
      finally { setLoading(false); }
    };
    fetchTechs();
  }, []);

  const fetchTechStock = useCallback(async (techId) => {
    try {
      const res = await axios.get(`${API}/inventory/technicians/${techId}/stock`, getAuthConfig());
      setTechStock(res.data);
    } catch { setTechStock([]); }
  }, []);

  const fetchItems = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/inventory/items`, getAuthConfig());
      setInventoryItems(res.data);
    } catch {}
  }, []);

  useEffect(() => { if (selectedTech) { fetchTechStock(selectedTech); fetchItems(); } }, [selectedTech, fetchTechStock, fetchItems]);

  const assignStock = async () => {
    if (!assignForm.item_id || assignForm.quantity < 1) { toast.error("Select item and quantity"); return; }
    try {
      await axios.post(`${API}/inventory/technicians/${selectedTech}/stock`, assignForm, getAuthConfig());
      toast.success("Stock assigned");
      setAssignOpen(false);
      fetchTechStock(selectedTech);
      fetchItems();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to assign stock"); }
  };

  const doTransfer = async () => {
    if (!transferForm.item_id || transferForm.quantity < 1) { toast.error("Select item and quantity"); return; }
    try {
      await axios.post(`${API}/inventory/transfer`, transferForm, getAuthConfig());
      toast.success("Transfer completed");
      setTransferOpen(false);
      if (selectedTech) fetchTechStock(selectedTech);
      fetchItems();
    } catch (e) { toast.error(e.response?.data?.detail || "Transfer failed"); }
  };

  const getInitials = (name) => name?.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2) || "?";
  const selectedTechData = technicians.find(t => (t.technician_id || t.member_id) === selectedTech);
  const totalTechItems = techStock.reduce((s, st) => s + (st.quantity || 0), 0);

  return (
    <div className="space-y-4" data-testid="tech-stock-tab">
      {/* Technician Cards */}
      {loading ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mr-2" />Loading...</div>
      ) : technicians.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
          <Wrench className="w-10 h-10 mb-3 opacity-30" /><p className="text-sm">No technicians found</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {technicians.map(tech => {
              const id = tech.technician_id || tech.member_id;
              const isSelected = id === selectedTech;
              return (
                <button
                  key={id}
                  onClick={() => setSelectedTech(isSelected ? null : id)}
                  className={`relative rounded-xl p-4 text-left transition-all duration-200 border ${
                    isSelected
                      ? "bg-[#0046BE]/10 border-[#0046BE]/40 shadow-lg shadow-[#0046BE]/5"
                      : "bg-card/60 backdrop-blur-md border-border/60 hover:border-border hover:translate-y-[-1px]"
                  }`}
                  data-testid={`tech-card-${id}`}
                >
                  {isSelected && <div className="absolute top-0 left-0 right-0 h-0.5 bg-[#0046BE]" />}
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-sm font-bold font-['Manrope'] mb-2 ${
                    isSelected ? "bg-[#0046BE]/20 text-[#0046BE]" : "bg-muted/60 text-muted-foreground"
                  }`}>
                    {getInitials(tech.name)}
                  </div>
                  <p className="text-sm font-medium text-foreground truncate">{tech.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{tech.email || tech.phone || ""}</p>
                </button>
              );
            })}
          </div>

          {/* Selected Tech Stock */}
          {selectedTech && (
            <div className="space-y-3">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl bg-card/60 backdrop-blur-md border border-border/60 p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#0046BE]/15 flex items-center justify-center text-sm font-bold text-[#0046BE] font-['Manrope']">
                    {getInitials(selectedTechData?.name)}
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground font-['Manrope']">{selectedTechData?.name}</h3>
                    <p className="text-xs text-muted-foreground">{techStock.length} items, {totalTechItems} total units</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => { setTransferForm({ item_id: "", quantity: 1, from_type: "warehouse", from_id: "", to_type: "technician", to_id: selectedTech, notes: "" }); setTransferOpen(true); }} className="h-9 border-border/40 bg-card/60" data-testid="transfer-btn">
                    <ArrowRightLeft className="w-3.5 h-3.5 mr-1.5" />Transfer
                  </Button>
                  <Button size="sm" onClick={() => { setAssignForm({ item_id: "", quantity: 1 }); setAssignOpen(true); }} className="h-9 bg-[#0046BE] text-white hover:bg-[#0046BE]/80" data-testid="assign-stock-btn">
                    <Plus className="w-3.5 h-3.5 mr-1.5" />Assign Stock
                  </Button>
                </div>
              </div>

              {techStock.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <Box className="w-10 h-10 mb-3 opacity-30" /><p className="text-sm">No stock assigned</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="tech-stock-grid">
                  {techStock.map(s => (
                    <div key={s.item_id} className="rounded-xl bg-card/60 backdrop-blur-md border border-border/60 p-4 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                        <Package className="w-5 h-5 text-emerald-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-foreground truncate">{s.item_name}</h4>
                        <div className="flex items-center gap-2 mt-0.5">
                          {s.item_sku && <span className="text-xs text-muted-foreground">{s.item_sku}</span>}
                          {s.item_category && <Badge className="bg-muted/60 text-muted-foreground text-[10px] border-0">{s.item_category}</Badge>}
                        </div>
                      </div>
                      <Badge className={`text-xs font-bold font-['Manrope'] ${s.quantity > 0 ? "bg-emerald-500/15 text-emerald-400 border-0" : "bg-red-500/15 text-red-400 border-0"}`}>
                        {s.quantity} {s.item_unit}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Assign Dialog */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className="bg-background border-theme text-foreground" data-testid="assign-dialog">
          <DialogHeader><DialogTitle className="font-['Manrope']">Assign Stock from Warehouse</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-muted-foreground font-medium">Item</label>
              <Select value={assignForm.item_id || "none"} onValueChange={v => setAssignForm({...assignForm, item_id: v === "none" ? "" : v})}>
                <SelectTrigger className="bg-muted/50 border-border/40 h-9 mt-1" data-testid="assign-item-select"><SelectValue placeholder="Select Item" /></SelectTrigger>
                <SelectContent className="bg-popover border-theme">
                  <SelectItem value="none">Select Item</SelectItem>
                  {inventoryItems.map(i => <SelectItem key={i.item_id} value={i.item_id}>{i.name} (Stock: {i.stock_quantity})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground font-medium">Quantity</label>
              <Input type="number" min={1} value={assignForm.quantity} onChange={e => setAssignForm({...assignForm, quantity: parseInt(e.target.value) || 1})} className="bg-muted/50 border-border/40 h-9 mt-1" data-testid="assign-qty" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignOpen(false)} className="border-theme h-9">Cancel</Button>
            <Button onClick={assignStock} className="bg-[#0046BE] text-white hover:bg-[#0046BE]/80 h-9" data-testid="assign-confirm">Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transfer Dialog */}
      <Dialog open={transferOpen} onOpenChange={setTransferOpen}>
        <DialogContent className="bg-background border-theme text-foreground" data-testid="transfer-dialog">
          <DialogHeader><DialogTitle className="font-['Manrope']">Transfer Stock</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-muted-foreground font-medium">Item</label>
              <Select value={transferForm.item_id || "none"} onValueChange={v => setTransferForm({...transferForm, item_id: v === "none" ? "" : v})}>
                <SelectTrigger className="bg-muted/50 border-border/40 h-9 mt-1"><SelectValue placeholder="Select Item" /></SelectTrigger>
                <SelectContent className="bg-popover border-theme">
                  <SelectItem value="none">Select Item</SelectItem>
                  {inventoryItems.map(i => <SelectItem key={i.item_id} value={i.item_id}>{i.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground font-medium">From</label>
                <Select value={transferForm.from_type} onValueChange={v => setTransferForm({...transferForm, from_type: v, from_id: v === "warehouse" ? "" : transferForm.from_id})}>
                  <SelectTrigger className="bg-muted/50 border-border/40 h-9 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-popover border-theme">
                    <SelectItem value="warehouse">Warehouse</SelectItem>
                    <SelectItem value="technician">Technician</SelectItem>
                  </SelectContent>
                </Select>
                {transferForm.from_type === "technician" && (
                  <Select value={transferForm.from_id || "none"} onValueChange={v => setTransferForm({...transferForm, from_id: v === "none" ? "" : v})}>
                    <SelectTrigger className="bg-muted/50 border-border/40 h-9 mt-1"><SelectValue placeholder="Select Tech" /></SelectTrigger>
                    <SelectContent className="bg-popover border-theme">
                      <SelectItem value="none">Select</SelectItem>
                      {technicians.map(t => <SelectItem key={t.technician_id || t.member_id} value={t.technician_id || t.member_id}>{t.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </div>
              <div>
                <label className="text-xs text-muted-foreground font-medium">To</label>
                <Select value={transferForm.to_type} onValueChange={v => setTransferForm({...transferForm, to_type: v, to_id: v === "warehouse" ? "" : transferForm.to_id})}>
                  <SelectTrigger className="bg-muted/50 border-border/40 h-9 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-popover border-theme">
                    <SelectItem value="warehouse">Warehouse</SelectItem>
                    <SelectItem value="technician">Technician</SelectItem>
                  </SelectContent>
                </Select>
                {transferForm.to_type === "technician" && (
                  <Select value={transferForm.to_id || "none"} onValueChange={v => setTransferForm({...transferForm, to_id: v === "none" ? "" : v})}>
                    <SelectTrigger className="bg-muted/50 border-border/40 h-9 mt-1"><SelectValue placeholder="Select Tech" /></SelectTrigger>
                    <SelectContent className="bg-popover border-theme">
                      <SelectItem value="none">Select</SelectItem>
                      {technicians.map(t => <SelectItem key={t.technician_id || t.member_id} value={t.technician_id || t.member_id}>{t.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground font-medium">Quantity</label>
              <Input type="number" min={1} value={transferForm.quantity} onChange={e => setTransferForm({...transferForm, quantity: parseInt(e.target.value) || 1})} className="bg-muted/50 border-border/40 h-9 mt-1" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground font-medium">Notes</label>
              <Input value={transferForm.notes} onChange={e => setTransferForm({...transferForm, notes: e.target.value})} className="bg-muted/50 border-border/40 h-9 mt-1" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTransferOpen(false)} className="border-theme h-9">Cancel</Button>
            <Button onClick={doTransfer} className="bg-[#0046BE] text-white hover:bg-[#0046BE]/80 h-9" data-testid="transfer-confirm">Transfer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};


/* ══════════════ TRANSFERS TAB ══════════════ */
const TransfersTab = () => {
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/inventory/transfers`, getAuthConfig());
        setTransfers(res.data);
      } catch {}
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  return (
    <div data-testid="transfers-tab">
      {loading ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mr-2" />Loading...</div>
      ) : transfers.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
          <div className="w-16 h-16 rounded-2xl bg-muted/30 flex items-center justify-center mb-4"><ArrowRightLeft className="w-8 h-8 opacity-30" /></div>
          <p className="text-sm font-medium">No transfers yet</p>
          <p className="text-xs mt-1 opacity-60">Transfers between warehouse and technicians will appear here</p>
        </div>
      ) : (
        <div className="space-y-2">
          {transfers.map((t, idx) => (
            <div
              key={t.transfer_id}
              className="rounded-xl bg-card/60 backdrop-blur-md border border-border/60 p-4 flex items-center gap-4 transition-all hover:border-border"
              style={{ animationDelay: `${idx * 30}ms` }}
            >
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${t.to_type === "job" ? "bg-blue-500/10 text-blue-400" : "bg-emerald-500/10 text-emerald-400"}`}>
                <ArrowRightLeft className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-foreground">{t.item_name}</p>
                  <Badge className="bg-muted/60 text-muted-foreground text-[10px] border-0">x{t.quantity}</Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  <span className="text-foreground/70">{t.from_name}</span>
                  <span className="mx-1.5 text-muted-foreground">&rarr;</span>
                  <span className="text-foreground/70">{t.to_type === "job" ? `Job ${t.to_id}` : t.to_name}</span>
                </p>
              </div>
              {t.notes && <p className="text-xs text-muted-foreground truncate max-w-[160px] hidden sm:block">{t.notes}</p>}
              <span className="text-xs text-muted-foreground whitespace-nowrap">
                {t.created_at ? new Date(t.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : ""}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


/* ══════════════════════ MAIN PAGE ══════════════════════ */
const InventoryPage = () => {
  const [activeTab, setActiveTab] = useState("items");
  const { hasFeature, loading: limitsLoading } = usePlanLimits();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [stats, setStats] = useState({ total: 0, lowStock: 0, totalValue: 0, categories: 0 });
  const [storeLocations, setStoreLocations] = useState([]);

  const hasAccess = hasFeature("inventory_management");
  const isStoreAccount = user?.account_type === "multi_location_store";

  // Fetch store locations for store accounts
  useEffect(() => {
    if (!isStoreAccount) return;
    axios.get(`${API}/store/locations`, getAuthConfig())
      .then(res => setStoreLocations((res.data.locations || []).filter(l => l.active)))
      .catch(() => {});
  }, [isStoreAccount]);

  const tabs = [
    { id: "items", label: "Items", icon: Package },
    { id: "technicians", label: "Tech Stock", icon: Wrench },
    { id: "transfers", label: "Transfers", icon: ArrowRightLeft },
    // Owner-only cleanup tab — bulk delete by selection / category /
    // location / wipe-all. Backend enforces the owner check too.
    { id: "cleanup", label: "Cleanup", icon: Eraser },
  ];

  useEffect(() => {
    if (!hasAccess) return;
    const fetchStats = async () => {
      try {
        const [itemsRes, catsRes, lowRes] = await Promise.all([
          axios.get(`${API}/inventory/items`, getAuthConfig()),
          axios.get(`${API}/inventory/categories`, getAuthConfig()),
          axios.get(`${API}/inventory/low-stock-summary`, getAuthConfig()),
        ]);
        const items = itemsRes.data;
        setStats({
          total: items.length,
          lowStock: lowRes.data.length,
          totalValue: items.reduce((s, i) => s + (i.unit_price || 0) * (i.stock_quantity || 0), 0),
          categories: catsRes.data.length,
        });
      } catch {}
    };
    fetchStats();
  }, [hasAccess, activeTab]);

  if (limitsLoading) {
    return <div className="flex items-center justify-center h-[60vh]"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  }

  if (!hasAccess) {
    return (
      <div className="flex items-center justify-center h-[70vh] p-4" data-testid="inventory-upgrade-wall">
        <div className="text-center max-w-md space-y-6">
          <div className="mx-auto w-20 h-20 rounded-2xl bg-gradient-to-br from-orange-500/20 to-amber-500/20 flex items-center justify-center">
            <Lock className="w-9 h-9 text-orange-400" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-foreground font-['Manrope']">Inventory Management</h2>
            <p className="text-muted-foreground mt-2 text-sm leading-relaxed">Track parts, manage technician stock, and automate inventory deductions when jobs are completed. Upgrade your plan to unlock this feature.</p>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-muted/30 rounded-lg p-3 text-center"><Package className="w-5 h-5 text-blue-400 mx-auto mb-1.5" /><p className="text-xs text-muted-foreground">Central Inventory</p></div>
            <div className="bg-muted/30 rounded-lg p-3 text-center"><Wrench className="w-5 h-5 text-green-400 mx-auto mb-1.5" /><p className="text-xs text-muted-foreground">Tech Stock</p></div>
            <div className="bg-muted/30 rounded-lg p-3 text-center"><Sparkles className="w-5 h-5 text-amber-400 mx-auto mb-1.5" /><p className="text-xs text-muted-foreground">Auto-Deduct</p></div>
          </div>
          <Button onClick={() => navigate("/dashboard/settings")} className="bg-[#0046BE] text-white hover:bg-[#0046BE]/80 px-8" data-testid="upgrade-plan-btn"><Sparkles className="w-4 h-4 mr-2" />Upgrade Plan</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5 p-4 sm:p-6" data-testid="inventory-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground font-['Manrope']">Inventory</h1>
          <p className="text-muted-foreground text-sm mt-0.5">Manage parts, assign to technicians, track stock</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="rounded-xl bg-card/60 backdrop-blur-md border border-border/60 p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-[#0046BE]/10 flex items-center justify-center"><Package className="w-4 h-4 text-[#0046BE]" /></div>
          </div>
          <p className="text-2xl font-bold text-foreground font-['Manrope']" data-testid="stat-total">{stats.total}</p>
          <p className="text-xs text-muted-foreground">Total Items</p>
        </div>
        <div className="rounded-xl bg-card/60 backdrop-blur-md border border-border/60 p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${stats.lowStock > 0 ? "bg-orange-500/10" : "bg-emerald-500/10"}`}>
              <AlertTriangle className={`w-4 h-4 ${stats.lowStock > 0 ? "text-orange-400" : "text-emerald-400"}`} />
            </div>
          </div>
          <p className={`text-2xl font-bold font-['Manrope'] ${stats.lowStock > 0 ? "text-orange-400" : "text-foreground"}`} data-testid="stat-low">{stats.lowStock}</p>
          <p className="text-xs text-muted-foreground">Low Stock</p>
        </div>
        <div className="rounded-xl bg-card/60 backdrop-blur-md border border-border/60 p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center"><DollarSign className="w-4 h-4 text-emerald-400" /></div>
          </div>
          <p className="text-2xl font-bold text-foreground font-['Manrope']" data-testid="stat-value">${stats.totalValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
          <p className="text-xs text-muted-foreground">Total Value</p>
        </div>
        <div className="rounded-xl bg-card/60 backdrop-blur-md border border-border/60 p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-violet-500/10 flex items-center justify-center"><Tag className="w-4 h-4 text-violet-400" /></div>
          </div>
          <p className="text-2xl font-bold text-foreground font-['Manrope']" data-testid="stat-categories">{stats.categories}</p>
          <p className="text-xs text-muted-foreground">Categories</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-card/40 backdrop-blur-sm border border-border/40 rounded-xl p-1 w-fit">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
              activeTab === tab.id
                ? "bg-[#0046BE] text-white shadow-md shadow-[#0046BE]/20"
                : "text-muted-foreground hover:text-foreground hover:bg-muted/30"
            }`}
            data-testid={`inventory-tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" /><span className="hidden sm:inline">{tab.label}</span>
          </button>
        ))}
      </div>

      {activeTab === "items" && <ItemsTab isStoreAccount={isStoreAccount} storeLocations={storeLocations} />}
      {activeTab === "technicians" && <TechStockTab />}
      {activeTab === "transfers" && <TransfersTab />}
      {activeTab === "cleanup" && <CleanupTab isStoreAccount={isStoreAccount} storeLocations={storeLocations} />}
    </div>
  );
};

export default InventoryPage;
